import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { HStack } from 'native-base';
import moment from 'moment';

import { Button, OpsbeeWordmarkIcon, PrinterIcon, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { CleaningScheduleTypeEnum } from '@pimm/services/lib/sms-workforce';
import { StringTranslation } from '@pimm/services/lib/sms-workforce/types';

import { useSiteConfig } from '@app/features/store-core';
import { ScheduleTypeCategoriesReturn } from '@app/features/cleaning-tasks/hooks/get-cleaning-plan.hook';
import { formatTo } from '@app/utils/date-formatter';

// Platform: web — uses window.print(), document.querySelector, and @media print CSS
// Purpose: printable cleaning plan rendered as a landscape letter-size paper layout
const PRINT_STYLES = `
  .cleaning-report-screen-wrap {
    background: #f3f4f6;
    padding: 24px 0;
    overflow-y: auto;
    max-height: calc(90vh - 56px);
  }
  .cleaning-report-paper {
    width: 11in;
    height: 8.5in;
    margin: 0 auto;
    background: white;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.12);
    padding: 0.4in 0.45in;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .cleaning-report-paper + .cleaning-report-paper { margin-top: 24px; }
  .cleaning-report-paper-body { flex: 1 1 auto; min-height: 0; overflow: hidden; font-family: 'Inter', sans-serif; }

  .cleaning-report-page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    margin-bottom: 10px;
    flex-shrink: 0;
  }
  .cleaning-report-brand-logo {
    height: 44px;
    width: auto;
    object-fit: contain;
  }
  .cleaning-report-page-title {
    font-family: 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: #111827;
    line-height: 1;
    letter-spacing: -0.2px;
  }
  .cleaning-report-page-context {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    text-align: right;
    line-height: 1.4;
  }

  /* Summary band: store info on left, completion totals on right.
     Sits between the page header and the section header on every page. */
  .cleaning-report-summary-band {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 6px;
    margin-bottom: 8px;
    padding: 8px 12px;
    background: #F3F3F3;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    border-radius: 4px;
    font-family: 'Inter', sans-serif;
    gap: 16px;
  }
  .cleaning-report-summary-store {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
    min-width: 0;
  }
  .cleaning-report-summary-customer {
    font-size: 9px;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .cleaning-report-summary-name-row {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }
  .cleaning-report-summary-dot {
    font-size: 12px;
    color: #9ca3af;
    line-height: 1;
  }
  .cleaning-report-summary-store-name {
    font-size: 12px;
    font-weight: 700;
    color: #111827;
  }
  .cleaning-report-summary-contact {
    font-size: 12px;
    font-weight: 600;
    color: #111827;
  }
  .cleaning-report-summary-address {
    font-size: 10px;
    color: #4b5563;
  }
  .cleaning-report-summary-totals {
    display: flex;
    flex-direction: row;
    gap: 14px;
    align-items: center;
  }
  .cleaning-report-summary-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    min-width: 56px;
  }
  .cleaning-report-summary-stat-label {
    font-size: 9px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 2px;
  }
  .cleaning-report-summary-stat-value {
    font-size: 13px;
    font-weight: 800;
    color: #111827;
  }
  .cleaning-report-summary-stat-fraction {
    font-size: 9px;
    color: #6b7280;
    font-weight: 600;
  }

  .cleaning-report-page-footer {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    padding-top: 6px;
    margin-top: 6px;
    font-family: 'Inter', sans-serif;
    font-size: 9px;
    color: #6b7280;
    flex-shrink: 0;
  }
  .cleaning-report-page-footer > *:nth-child(2) { text-align: center; }
  .cleaning-report-page-footer > *:nth-child(3) { text-align: right; }

  .cleaning-report-section-bar {
    padding: 4px 0 8px;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    line-height: 20px;
    font-weight: 800;
    color: #111827;
  }
  .cleaning-report-section-bar.is-muted { color: #6b7280; font-style: italic; }

  /* Daily grid */
  .cleaning-report-daily-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-family: 'Inter', sans-serif;
    margin-bottom: 12px;
    border: 1px solid #e5e7eb;
  }
  .cleaning-report-daily-table th,
  .cleaning-report-daily-table td {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
    padding: 6px 8px;
  }
  .cleaning-report-daily-table th:last-child,
  .cleaning-report-daily-table td:last-child { border-right: none; }
  .cleaning-report-daily-table tr:last-child td { border-bottom: none; }
  .cleaning-report-daily-category-row th {
    background: #5B5B5B;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color: white;
    font-size: 11.5px;
    font-weight: 700;
    padding: 7px 12px;
    text-align: left;
    border-right: none;
    letter-spacing: 0.3px;
  }
  .cleaning-report-daily-day-header th {
    background: #D8D9DA;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 10.5px;
    font-weight: 700;
    color: #1f2937;
    text-align: left;
    padding: 7px 8px;
  }
  .cleaning-report-daily-position-cell {
    background: #F3F3F3;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 10.5px;
    font-weight: 600;
    color: #1f2937;
    width: 12%;
    line-height: 1.25;
  }
  .cleaning-report-daily-cell { color: #111827; line-height: 1.3; }
  .cleaning-report-daily-cell-empty { background: #fafafa; color: #d1d5db; text-align: center; font-size: 10px; }
  .cleaning-report-daily-task-title { font-weight: 600; font-size: 10px; color: #111827; line-height: 1.3; }
  .cleaning-report-daily-task-assignee {
    color: #6b7280;
    font-size: 9.5px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cleaning-report-daily-task-unassigned {
    color: #dc2626;
    font-size: 9.5px;
    line-height: 1.3;
    font-weight: 600;
    font-style: italic;
  }
  .cleaning-report-daily-task-complete {
    color: #111827;
    font-size: 9.5px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
  }
  .cleaning-report-status-check {
    color: #2563eb;
    font-weight: 700;
    margin-right: 3px;
  }

  /* Section-scoped completion accent */
  .cleaning-report-paper-body--daily .cleaning-report-status-check,
  .cleaning-report-paper-body--daily .cleaning-report-daily-task-complete,
  .cleaning-report-paper-body--daily .cleaning-report-list-complete { color: #2563eb; }

  .cleaning-report-paper-body--weekly .cleaning-report-status-check,
  .cleaning-report-paper-body--weekly .cleaning-report-daily-task-complete,
  .cleaning-report-paper-body--weekly .cleaning-report-list-complete { color: #16a34a; }

  .cleaning-report-paper-body--monthly .cleaning-report-status-check,
  .cleaning-report-paper-body--monthly .cleaning-report-daily-task-complete,
  .cleaning-report-paper-body--monthly .cleaning-report-list-complete { color: #9333ea; }
  .cleaning-report-status-na {
    font-size: 9.5px;
    color: #6b7280;
    font-style: italic;
    font-weight: 600;
    line-height: 1.3;
  }

  .cleaning-report-placeholder {
    border: 1px dashed #d1d5db;
    border-radius: 4px;
    padding: 18px;
    text-align: center;
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    color: #9ca3af;
    font-style: italic;
    margin-bottom: 12px;
  }

  /* Two-column wrapper (Weekly + Monthly) */
  .cleaning-report-list-two-col {
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: flex-start;
  }
  .cleaning-report-list-two-col > .cleaning-report-list-table,
  .cleaning-report-list-two-col > .cleaning-report-list-col-empty {
    flex: 0 0 calc(50% - 6px);
    max-width: calc(50% - 6px);
    margin-bottom: 0;
  }
  .cleaning-report-list-col-empty {
    /* reserves the right-column width so the left table stays at 50% */
  }

  /* List grid (Weekly + Monthly) */
  .cleaning-report-list-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-family: 'Inter', sans-serif;
    margin-bottom: 12px;
    border: 1px solid #e5e7eb;
  }
  .cleaning-report-list-table th,
  .cleaning-report-list-table td {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
    padding: 4px 8px;
  }
  .cleaning-report-list-table th:last-child,
  .cleaning-report-list-table td:last-child { border-right: none; }
  .cleaning-report-list-table tr:last-child td { border-bottom: none; }
  .cleaning-report-list-category-row th {
    background: #5B5B5B;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color: white;
    font-size: 11.5px;
    font-weight: 700;
    padding: 7px 12px;
    text-align: left;
    border-right: none;
    letter-spacing: 0.3px;
  }
  .cleaning-report-list-column-header th {
    background: #D8D9DA;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 10.5px;
    font-weight: 700;
    color: #1f2937;
    text-align: left;
    padding: 7px 10px;
  }
  .cleaning-report-list-task-title {
    font-size: 10.5px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }
  .cleaning-report-list-due-date {
    font-size: 10px;
    color: #374151;
    line-height: 1.3;
    white-space: nowrap;
  }
  .cleaning-report-list-assignee {
    font-size: 10px;
    color: #4b5563;
    line-height: 1.3;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .cleaning-report-list-unassigned {
    font-size: 10px;
    color: #dc2626;
    font-weight: 600;
    font-style: italic;
    line-height: 1.3;
  }
  .cleaning-report-list-complete {
    font-size: 10px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    line-height: 1.3;
  }
  .cleaning-report-list-complete-empty {
    font-size: 10px;
    color: #d1d5db;
  }
  .cleaning-report-list-schedule { line-height: 1.25; }
  .cleaning-report-list-schedule .cleaning-report-list-due-date { font-size: 9.5px; color: #6b7280; }
  .cleaning-report-list-schedule .cleaning-report-list-complete { margin-top: 1px; }

  @media print {
    @page { size: letter landscape; margin: 0; }
    html, body {
      background: white !important;
      margin: 0 !important;
      height: auto !important;
      overflow: visible !important;
    }
    body > *:not(.cleaning-report-print-modal) { display: none !important; }
    .cleaning-report-print-modal {
      display: block !important;
      position: static !important;
      width: 100% !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      background: white !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .cleaning-report-print-chrome { display: none !important; }
    .cleaning-report-screen-wrap {
      padding: 0 !important;
      background: white !important;
      overflow: visible !important;
      max-height: none !important;
    }
    .cleaning-report-paper {
      box-shadow: none !important;
      margin: 0 !important;
    }
    .cleaning-report-paper + .cleaning-report-paper {
      margin-top: 0 !important;
      page-break-before: always;
      break-before: page;
    }
  }
`;

type CellTask = {
  title: string;
  titleTranslations?: StringTranslation[];
  positionTitle: string;
  status?: string;
  assigneeFirstName?: string;
  assigneeLastName?: string;
  statusDate?: string;
};

type DailyCategoryShape = {
  categoryTitle: string;
  categoryTitleTranslations?: StringTranslation[];
  positionsOrder: string[];
  positionTranslations: Record<string, StringTranslation[] | undefined>;
  cells: Record<string, Record<string, CellTask[]>>; // [position][dayName] = tasks
};

type ListTask = {
  title: string;
  titleTranslations?: StringTranslation[];
  dueDate?: string;
  status?: string;
  assigneeFirstName?: string;
  assigneeLastName?: string;
  statusDate?: string;
};

type ListCategoryShape = {
  categoryTitle: string;
  categoryTitleTranslations?: StringTranslation[];
  tasks: ListTask[];
};


export type PrintCleaningPlanProps = {
  cleaningPlan: ScheduleTypeCategoriesReturn[];
  week?: { startDate: Date; endDate: Date; weekNumber: number };
  generatedBy?: string;
  onClose: () => void;
};

export const PrintCleaningPlan = ({ cleaningPlan, week, generatedBy, onClose }: PrintCleaningPlanProps) => {
  const { t } = useTranslation();
  const { translate, locale } = useAppLocale();
  const isReady = useBoolean(false);
  const { siteConfig } = useSiteConfig();

  const brandLogoUrl: string = siteConfig?.companyInfo?.brand?.logoImgUrl ?? 'https://sms-cdn.b-cdn.net/assets-web/wendys/wendys-logo.png';

  const weekDayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, day) => {
        const date = week?.startDate.addDays(day);
        return {
          key: moment(date).locale('en-US').format('dddd'),
          label: moment(date).locale(locale).format('ddd'),
        };
      }),
    [week?.startDate, locale],
  );

  const dailyCategories = useMemo<DailyCategoryShape[]>(() => {
    const dailyEntries = (cleaningPlan ?? []).filter(p => p?.scheduleType === CleaningScheduleTypeEnum.Daily);
    const byTitle = new Map<string, DailyCategoryShape>();
    for (const entry of dailyEntries) {
      const dayName = entry.date instanceof Date ? moment(entry.date).locale('en-US').format('dddd') : '';
      for (const category of entry.categories ?? []) {
        const catTitle = category.title ?? 'Uncategorized';
        if (!byTitle.has(catTitle)) {
          byTitle.set(catTitle, {
            categoryTitle: catTitle,
            categoryTitleTranslations: category.translations,
            positionsOrder: [],
            positionTranslations: {},
            cells: {},
          });
        }
        const cat = byTitle.get(catTitle)!;
        for (const schedule of category.schedules ?? []) {
          for (const task of schedule.cleaningTasks ?? []) {
            const positionTitle = task?.position?.title ?? 'Unassigned';
            if (!cat.positionsOrder.includes(positionTitle)) {
              cat.positionsOrder.push(positionTitle);
              cat.positionTranslations[positionTitle] = task?.position?.translations;
            }
            if (!cat.cells[positionTitle]) cat.cells[positionTitle] = {};
            if (!cat.cells[positionTitle][dayName]) cat.cells[positionTitle][dayName] = [];
            const a = task.cleaningTaskAssignment;
            cat.cells[positionTitle][dayName].push({
              title: task.title ?? '',
              titleTranslations: task.titleTranslations,
              positionTitle,
              status: a?.cleaningTaskStatus,
              assigneeFirstName: a?.firstName,
              assigneeLastName: a?.lastName,
              statusDate: a?.statusDate,
            });
          }
        }
      }
    }
    return Array.from(byTitle.values());
  }, [cleaningPlan]);

  const buildListCategories = (scheduleType: string): ListCategoryShape[] => {
    const block = (cleaningPlan ?? []).find(p => p?.scheduleType === scheduleType);
    if (!block) return [];
    return (block.categories ?? []).map((category: any) => ({
      categoryTitle: category.title ?? 'Uncategorized',
      categoryTitleTranslations: category.translations,
      tasks: (category.schedules ?? []).flatMap((s: any) =>
        (s.cleaningTasks ?? []).map((task: any) => {
          const a = task.cleaningTaskAssignment;
          return {
            title: task.title ?? '',
            titleTranslations: task.titleTranslations,
            dueDate: a?.dueDate,
            status: a?.cleaningTaskStatus,
            assigneeFirstName: a?.firstName,
            assigneeLastName: a?.lastName,
            statusDate: a?.statusDate,
          } as ListTask;
        }),
      ),
    }));
  };

  const weeklyCategories = useMemo(() => buildListCategories(CleaningScheduleTypeEnum.Weekly), [cleaningPlan]);
  const monthlyCategories = useMemo(() => buildListCategories(CleaningScheduleTypeEnum.Monthly), [cleaningPlan]);

  // Completion totals per schedule type — used in the summary band.
  // N/A tasks are EXCLUDED from both numerator and denominator.
  const computeStats = (scheduleType: string) => {
    let total = 0;
    let completed = 0;
    for (const entry of cleaningPlan ?? []) {
      if (entry?.scheduleType !== scheduleType) continue;
      for (const cat of entry.categories ?? []) {
        for (const sched of cat.schedules ?? []) {
          for (const task of sched.cleaningTasks ?? []) {
            const status = task?.cleaningTaskAssignment?.cleaningTaskStatus;
            if (status === 'NotApplicable') continue;
            total++;
            if (status === 'Complete') completed++;
          }
        }
      }
    }
    return { total, completed };
  };
  const dailyStats = useMemo(() => computeStats(CleaningScheduleTypeEnum.Daily), [cleaningPlan]);
  const weeklyStats = useMemo(() => computeStats(CleaningScheduleTypeEnum.Weekly), [cleaningPlan]);
  const monthlyStats = useMemo(() => computeStats(CleaningScheduleTypeEnum.Monthly), [cleaningPlan]);

  // Daily section overflow: chunk a category's positions across multiple papers
  const MAX_POSITIONS_PER_PAGE = 7;
  const chunkPositionsForCategory = (cat: DailyCategoryShape): DailyCategoryShape[] => {
    if (cat.positionsOrder.length <= MAX_POSITIONS_PER_PAGE) return [cat];
    const totalChunks = Math.ceil(cat.positionsOrder.length / MAX_POSITIONS_PER_PAGE);
    const chunks: DailyCategoryShape[] = [];
    for (let i = 0; i < cat.positionsOrder.length; i += MAX_POSITIONS_PER_PAGE) {
      const slice = cat.positionsOrder.slice(i, i + MAX_POSITIONS_PER_PAGE);
      const chunkNum = Math.floor(i / MAX_POSITIONS_PER_PAGE) + 1;
      const cells: typeof cat.cells = {};
      const positionTranslations: typeof cat.positionTranslations = {};
      slice.forEach(pos => {
        cells[pos] = cat.cells[pos];
        positionTranslations[pos] = cat.positionTranslations[pos];
      });
      chunks.push({
        categoryTitle: `${cat.categoryTitle} (${chunkNum} ${t('cleaning_task:of')} ${totalChunks})`,
        positionsOrder: slice,
        positionTranslations,
        cells,
      });
    }
    return chunks;
  };

  const dailyPageChunks = useMemo(() => dailyCategories.flatMap(cat => chunkPositionsForCategory(cat)), [dailyCategories, t]);

  // Two-pass pagination for list-style sections (Weekly / Monthly).
  const MAX_TASKS_PER_COLUMN = 14;
  const MAX_TASKS_PER_PAGE = MAX_TASKS_PER_COLUMN * 2;

  const chunkIntoPages = (cats: ListCategoryShape[]): ListCategoryShape[][] => {
    if (cats.length === 0) return [];
    const pages: ListCategoryShape[][] = [];
    let current: ListCategoryShape[] = [];
    let currentCount = 0;
    for (const cat of cats) {
      const taskCount = cat.tasks.length;
      if (current.length > 0 && currentCount + taskCount > MAX_TASKS_PER_PAGE) {
        pages.push(current);
        current = [];
        currentCount = 0;
      }
      current.push(cat);
      currentCount += taskCount;
    }
    if (current.length > 0) pages.push(current);
    return pages;
  };

  const splitIntoTwoColumns = (cats: ListCategoryShape[]): [ListCategoryShape[], ListCategoryShape[]] => {
    if (cats.length === 0) return [[], []];
    let leftCount = 0;
    let splitIdx = cats.length;
    for (let i = 0; i < cats.length; i++) {
      const nextCount = leftCount + cats[i].tasks.length;
      if (leftCount > 0 && nextCount > MAX_TASKS_PER_COLUMN) {
        splitIdx = i;
        break;
      }
      leftCount = nextCount;
    }
    return [cats.slice(0, splitIdx), cats.slice(splitIdx)];
  };

  const weeklyPageChunks = useMemo(() => chunkIntoPages(weeklyCategories).map(splitIntoTwoColumns), [weeklyCategories]);
  const monthlyPageChunks = useMemo(() => chunkIntoPages(monthlyCategories).map(splitIntoTwoColumns), [monthlyCategories]);

  const rangeLabel = week
    ? `Week ${week.weekNumber} · ${moment(week.startDate).format('MMM D')} – ${moment(week.endDate).format('MMM D, YYYY')}`
    : '';
  const generatedAt = moment().format('MMM D, YYYY h:mm A');
  const siteName = siteConfig?.name ?? '';
  const dailyPaperCount = dailyPageChunks.length;
  const weeklyPaperCount = weeklyPageChunks.length;
  const monthlyPaperCount = monthlyPageChunks.length;
  const totalPages = dailyPaperCount + weeklyPaperCount + monthlyPaperCount;
  const firstSectionWithData = dailyPageChunks.length > 0 ? 'daily' : weeklyPageChunks.length > 0 ? 'weekly' : 'monthly';

  const handlePrint = () => {
    const node = document.querySelector('.cleaning-report-print-modal');
    if (!node || !node.parentNode) {
      window.print();
      return;
    }
    const placeholder = document.createComment('print-modal-placeholder');
    node.parentNode.insertBefore(placeholder, node);
    document.body.appendChild(node);
    const restore = () => {
      window.removeEventListener('afterprint', restore);
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(node, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };
    window.addEventListener('afterprint', restore);
    window.print();
  };

  useEffect(() => {
    const timeoutRef = setTimeout(() => isReady.setTrue(), 800);
    return () => clearTimeout(timeoutRef);
  }, []);

  useEffect(() => {
    const handler = () => {};
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, []);

  if (!isReady.value) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: `@keyframes cleaningReportSpin { to { transform: rotate(360deg); } }` }} />
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '40px 60px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: '4px solid #E5E7EB',
                borderTopColor: '#FCB816',
                borderRadius: '50%',
                animation: 'cleaningReportSpin 0.8s linear infinite',
              }}
            />
            <div style={{ color: '#4B5563', fontSize: 14, fontWeight: 500 }}>{t('cleaning_task:generating_report')}</div>
          </div>
        </div>
      </>
    );
  }

  const renderPageHeader = (opts?: { withSummary?: boolean }) => (
    <>
      <div className="cleaning-report-page-header">
        <HStack alignItems="center" space={3} flex={1}>
          {brandLogoUrl ? <img src={brandLogoUrl} alt="Brand" className="cleaning-report-brand-logo" /> : null}
          <div className="cleaning-report-page-title">{t('cleaning_task:weekly_cleaning_chart')}</div>
        </HStack>
        <div className="cleaning-report-page-context">
          <div>{rangeLabel}</div>
          <div>{siteName}</div>
        </div>
      </div>
      {opts?.withSummary ? renderSummaryBand() : null}
    </>
  );

  const customerName = (siteConfig as any)?.companyInfo?.customerName ?? '';
  const address = (siteConfig as any)?.address;
  const addressLine = address
    ? [
        [address.address1, address.address2].filter(Boolean).join(' '),
        address.city,
        [address.state, address.postal].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  const primaryContact = (siteConfig as any)?.primaryContact;
  const primaryContactName = primaryContact ? [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ') : '';

  const pct = (s: { total: number; completed: number }) => (s.total === 0 ? 0 : Math.round((s.completed / s.total) * 100));

  const renderSummaryBand = () => (
    <div className="cleaning-report-summary-band">
      <div className="cleaning-report-summary-store">
        {customerName ? <div className="cleaning-report-summary-customer">{customerName}</div> : null}
        <div className="cleaning-report-summary-name-row">
          <div className="cleaning-report-summary-store-name">{siteName}</div>
          {primaryContactName ? (
            <>
              <span className="cleaning-report-summary-dot">•</span>
              <div className="cleaning-report-summary-contact">{primaryContactName}</div>
            </>
          ) : null}
        </div>
        {addressLine ? <div className="cleaning-report-summary-address">{addressLine}</div> : null}
      </div>
      <div className="cleaning-report-summary-totals">
        {dailyStats.total > 0 && (
          <div className="cleaning-report-summary-stat">
            <div className="cleaning-report-summary-stat-label">{t('cleaning_task:daily')}</div>
            <div className="cleaning-report-summary-stat-value">{pct(dailyStats)}%</div>
            <div className="cleaning-report-summary-stat-fraction">
              {dailyStats.completed} / {dailyStats.total}
            </div>
          </div>
        )}
        {weeklyStats.total > 0 && (
          <div className="cleaning-report-summary-stat">
            <div className="cleaning-report-summary-stat-label">{t('cleaning_task:weekly')}</div>
            <div className="cleaning-report-summary-stat-value">{pct(weeklyStats)}%</div>
            <div className="cleaning-report-summary-stat-fraction">
              {weeklyStats.completed} / {weeklyStats.total}
            </div>
          </div>
        )}
        {monthlyStats.total > 0 && (
          <div className="cleaning-report-summary-stat">
            <div className="cleaning-report-summary-stat-label">{t('cleaning_task:monthly')}</div>
            <div className="cleaning-report-summary-stat-value">{pct(monthlyStats)}%</div>
            <div className="cleaning-report-summary-stat-fraction">
              {monthlyStats.completed} / {monthlyStats.total}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPageFooter = (pageNumber: number) => (
    <div className="cleaning-report-page-footer">
      <span style={{ display: 'flex', alignItems: 'center', justifySelf: 'start' }}>
        <OpsbeeWordmarkIcon height={18} />
      </span>
      <span>{`${t('cleaning_task:generated_at')} ${generatedAt}${generatedBy ? ' · ' + generatedBy : ''}`}</span>
      <span>{`${t('cleaning_task:page')} ${pageNumber} ${t('cleaning_task:of')} ${totalPages}`}</span>
    </div>
  );

  const formatAssignee = (first?: string, last?: string) => {
    if (!last) return first ?? '';
    if (!first) return last;
    return `${first.charAt(0).toUpperCase()}. ${last}`;
  };

  const renderCell = (tasks: CellTask[] | undefined) => {
    if (!tasks || tasks.length === 0) {
      return <td className="cleaning-report-daily-cell cleaning-report-daily-cell-empty">—</td>;
    }
    return (
      <td className="cleaning-report-daily-cell">
        {tasks.map((task, idx) => {
          const assignee = formatAssignee(task.assigneeFirstName, task.assigneeLastName);
          const isComplete = task.status === 'Complete';
          const isNotApplicable = task.status === 'NotApplicable';
          const ts = isComplete && task.statusDate ? formatTo(task.statusDate, 'M/D h:mm A') : '';
          return (
            <div key={idx} style={{ marginBottom: idx < tasks.length - 1 ? 4 : 0 }}>
              <div className="cleaning-report-daily-task-title">{translate(task.title, task.titleTranslations)}</div>
              {isNotApplicable ? (
                <div className="cleaning-report-status-na">N/A</div>
              ) : (
                <>
                  {assignee ? (
                    <div className="cleaning-report-daily-task-assignee">{assignee}</div>
                  ) : (
                    <div className="cleaning-report-daily-task-unassigned">{t('cleaning_task:unassigned')}</div>
                  )}
                  {ts ? (
                    <div className="cleaning-report-daily-task-complete">
                      <span className="cleaning-report-status-check">✓</span>
                      {ts}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
        })}
      </td>
    );
  };

  const renderDailyCategoryTable = (cat: DailyCategoryShape) => (
    <table key={cat.categoryTitle} className="cleaning-report-daily-table">
      <thead>
        <tr className="cleaning-report-daily-category-row">
          <th colSpan={8}>{translate(cat.categoryTitle, cat.categoryTitleTranslations)}</th>
        </tr>
        <tr className="cleaning-report-daily-day-header">
          <th>{t('cleaning_task:position')}</th>
          {weekDayLabels.map(({ key, label }) => (
            <th key={key}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cat.positionsOrder.map(position => (
          <tr key={position}>
            <td className="cleaning-report-daily-position-cell">{translate(position, cat.positionTranslations?.[position])}</td>
            {weekDayLabels.map(({ key }) => (
              <React.Fragment key={key}>{renderCell(cat.cells[position]?.[key])}</React.Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderListTable = (categories: ListCategoryShape[]) => (
    <table className="cleaning-report-list-table">
      <colgroup>
        <col style={{ width: '45%' }} />
        <col style={{ width: '25%' }} />
        <col style={{ width: '30%' }} />
      </colgroup>
      <tbody>
        {categories.map(cat => (
          <React.Fragment key={cat.categoryTitle}>
            <tr className="cleaning-report-list-category-row">
              <th colSpan={3}>{translate(cat.categoryTitle, cat.categoryTitleTranslations)}</th>
            </tr>
            <tr className="cleaning-report-list-column-header">
              <th>{t('cleaning_task:task')}</th>
              <th>{t('cleaning_task:assignee')}</th>
              <th>{t('cleaning_task:schedule')}</th>
            </tr>
            {cat.tasks.map((task, idx) => {
              const assignee = formatAssignee(task.assigneeFirstName, task.assigneeLastName);
              const isComplete = task.status === 'Complete';
              const isNotApplicable = task.status === 'NotApplicable';
              const due = task.dueDate ? moment(task.dueDate).format('ddd, M/D') : '';
              const ts = isComplete && task.statusDate ? formatTo(task.statusDate, 'M/D h:mm A') : '';
              return (
                <tr key={`${cat.categoryTitle}-${idx}`}>
                  <td className="cleaning-report-list-task-title">{translate(task.title, task.titleTranslations)}</td>
                  <td>
                    {isNotApplicable ? (
                      <span className="cleaning-report-status-na">—</span>
                    ) : assignee ? (
                      <span className="cleaning-report-list-assignee">{assignee}</span>
                    ) : (
                      <span className="cleaning-report-list-unassigned">{t('cleaning_task:unassigned')}</span>
                    )}
                  </td>
                  <td className="cleaning-report-list-schedule">
                    {isNotApplicable ? (
                      <span className="cleaning-report-status-na">N/A</span>
                    ) : (
                      <>
                        {due ? <div className="cleaning-report-list-due-date">{`${t('cleaning_task:due_by')} ${due}`}</div> : null}
                        {ts ? (
                          <div className="cleaning-report-list-complete">
                            <span className="cleaning-report-status-check">✓</span>
                            {ts}
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />

      <div className="cleaning-report-print-modal">
        <div
          className="cleaning-report-print-chrome"
          style={{
            background: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderBottom: '1px solid #E5E7EB',
            padding: '8px 16px',
          }}
        >
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <HStack space={3} alignItems="center">
              <PrinterIcon color="gray.700" size={5} />
              <Text size="lg" fontWeight={700} color="black">
                {t('cleaning_task:print_preview')}
              </Text>
            </HStack>
            <HStack space={2}>
              <Button
                size="md"
                variant="unstyled"
                borderColor="gray.300"
                bg="white"
                _text={{ color: 'gray.900' }}
                shadow={1}
                onPress={onClose}
              >
                {t('cleaning_task:close')}
              </Button>
              <Button size="md" shadow={0} startIcon={<PrinterIcon color="black" size={4} />} onPress={handlePrint}>
                {t('cleaning_task:print')}
              </Button>
            </HStack>
          </HStack>
        </div>

        <div className="cleaning-report-screen-wrap">
          {/* Pages 1..N — one paper per Daily category */}
          {dailyPageChunks.map((cat, idx) => (
            <div className="cleaning-report-paper" key={`daily-${idx}-${cat.categoryTitle}`}>
              {renderPageHeader({ withSummary: firstSectionWithData === 'daily' && idx === 0 })}
              <div className="cleaning-report-paper-body cleaning-report-paper-body--daily">
                <div className="cleaning-report-section-bar">{t('cleaning_task:daily_tasks')}</div>
                {renderDailyCategoryTable(cat)}
              </div>
              {renderPageFooter(idx + 1)}
            </div>
          ))}

          {/* Weekly Tasks */}
          {weeklyPageChunks.map(([leftCol, rightCol], idx) => (
            <div className="cleaning-report-paper" key={`weekly-${idx}`}>
              {renderPageHeader({ withSummary: firstSectionWithData === 'weekly' && idx === 0 })}
              <div className="cleaning-report-paper-body cleaning-report-paper-body--weekly">
                <div className="cleaning-report-section-bar">
                  {t('cleaning_task:weekly_tasks')}
                  {weeklyPageChunks.length > 1 ? ` (${idx + 1} ${t('cleaning_task:of')} ${weeklyPageChunks.length})` : ''}
                </div>
                <div className="cleaning-report-list-two-col">
                  {renderListTable(leftCol)}
                  {rightCol.length > 0 ? renderListTable(rightCol) : <div className="cleaning-report-list-col-empty" />}
                </div>
              </div>
              {renderPageFooter(dailyPaperCount + idx + 1)}
            </div>
          ))}

          {/* Monthly Tasks */}
          {monthlyPageChunks.map(([leftCol, rightCol], idx) => (
            <div className="cleaning-report-paper" key={`monthly-${idx}`}>
              {renderPageHeader({ withSummary: firstSectionWithData === 'monthly' && idx === 0 })}
              <div className="cleaning-report-paper-body cleaning-report-paper-body--monthly">
                <div className="cleaning-report-section-bar">
                  {t('cleaning_task:monthly_tasks')}
                  {monthlyPageChunks.length > 1 ? ` (${idx + 1} ${t('cleaning_task:of')} ${monthlyPageChunks.length})` : ''}
                </div>
                <div className="cleaning-report-list-two-col">
                  {renderListTable(leftCol)}
                  {rightCol.length > 0 ? renderListTable(rightCol) : <div className="cleaning-report-list-col-empty" />}
                </div>
              </div>
              {renderPageFooter(dailyPaperCount + weeklyPaperCount + idx + 1)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
